import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogContent } from '@mui/material';
import { useSegment } from 'common/hooks/segment';
import { ModalHeader } from 'layouts/Generic/modal-header';
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { actOnCredentials, TerminalCredential } from 'services/clients/credentials';
import fonts from 'styles/typography.module.css';
import { QueryKeys } from '../../queries';

interface Props {
  credentials: TerminalCredential[];
  onClose: () => void;
}

export const DeleteCredential: React.FC<Props> = (props: Props) => {
  const { credentials, onClose } = props;
  const open = credentials.length > 0;

  const deleteMutation = useMutation(actOnCredentials);
  const queryClient = useQueryClient();
  const { track } = useSegment();

  const handleDelete = () => {
    deleteMutation.mutate(
      { credentials: credentials.map((c) => c.id), action: 'delete' },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QueryKeys.TerminalCredentials]);
          queryClient.invalidateQueries([QueryKeys.MissingCredentialsCount]);

          track(
            'User deleted credential',
            {
              credentials: credentials.map((c) => ({
                id: c.id,
                portal: c.portal.name,
                status: c.status,
              })),
              success: true,
              error: '',
            },
            { includeCompany: true, includeUser: true }
          );

          onClose();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          // eslint-disable-next-line no-console
          console.error(error);

          track(
            'User deleted credential',
            { portals: credentials, success: false, error: error.message },
            { includeCompany: true, includeUser: true }
          );
        },
      }
    );
  };

  React.useEffect(() => {
    deleteMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} className={fonts.font}>
      <ModalHeader onClose={onClose}>
        <p className="font-bold">Delete credential</p>
      </ModalHeader>
      <DialogContent dividers className="space-y-8">
        <div>
          <p>
            Are you sure you want to delete{' '}
            {credentials.length === 1 ? 'this credential?' : 'the selected credentials?'}{' '}
          </p>
          {deleteMutation.isError && (
            /* eslint-disable-next-line */
            /* @ts-ignore */
            <p className="text-red-900">{deleteMutation.error?.message}</p>
          )}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onClose();
          }}
          className="flex justify-between"
        >
          <LoadingButton
            variant="contained"
            color="error"
            loading={deleteMutation.isLoading}
            onClick={handleDelete}
          >
            Yes, delete
          </LoadingButton>
          <Button variant="outlined" color="info" type="submit">
            Close
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
